"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  Maximize2,
  Minimize2,
  BookOpen,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import { getCategoryTheme } from "@/lib/tile-categories";

interface ToolSwitcherItem {
  id: string;
  label: string;
}

const TOOL_SWITCHER_ITEMS: ToolSwitcherItem[] = [
  { id: "json-explorer", label: "JSON-Explorer" },
  { id: "cron-planner", label: "Cron-Prüfstand" },
  { id: "image-redactor", label: "Bild-Schwärzer" },
  { id: "qr-code-generator", label: "QR-Studio" },
  { id: "diff-checker", label: "Diff-Checker" },
  { id: "link-preview", label: "Link-Vorschau" },
];

interface Props {
  id: string;
  title: string;
  tagline: string;
  statusBadge: string;
  category?: string;
  locale?: "de" | "en";
  standalone?: boolean;
  children: React.ReactNode;
}

export default function TileToolStage({
  id,
  title,
  tagline,
  statusBadge,
  category,
  locale = "de",
  standalone = false,
  children,
}: Props) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isEn = locale === "en";
  const theme = getCategoryTheme(category);

  useEffect(() => {
    if (!isFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isFullscreen]);

  const scrollToGuide = () => {
    const el = document.getElementById("guide-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="tool-hero-stage"
      aria-label={title}
      className={
        isFullscreen
          ? "fixed inset-0 z-[70] h-dvh w-screen overflow-y-auto bg-neutral-50 px-4 py-5 sm:px-8 sm:py-6 dark:bg-neutral-950"
          : standalone
            ? "mx-auto w-full max-w-[1680px]"
            : "mx-auto my-10 sm:my-12 w-full max-w-6xl min-[1320px]:max-w-[calc(100vw-29rem)] 2xl:max-w-[min(86rem,calc(100vw-34rem))] scroll-mt-24"
      }
    >
      {/* Standalone Mode Top Breadcrumb & Header (cleanly below 56px NavBar) */}
      {standalone && !isFullscreen && (
        <div className="mb-6">
          <nav
            aria-label="Breadcrumb"
            className="mb-4 flex flex-wrap items-center justify-between gap-3 text-xs"
          >
            <ol className="flex flex-wrap items-center gap-2 text-neutral-500 dark:text-neutral-400">
              <li>
                <Link
                  href={isEn ? "/en" : "/"}
                  className="transition-colors hover:text-neutral-900 dark:hover:text-white"
                >
                  {isEn ? "Home" : "Startseite"}
                </Link>
              </li>
              <li aria-hidden="true" className="select-none text-neutral-300 dark:text-neutral-700">
                /
              </li>
              <li>
                <Link
                  href={isEn ? "/en/garden" : "/garden"}
                  className="transition-colors hover:text-neutral-900 dark:hover:text-white"
                >
                  Digital Garden
                </Link>
              </li>
              <li aria-hidden="true" className="select-none text-neutral-300 dark:text-neutral-700">
                /
              </li>
              <li>
                <Link
                  href={`/garden/${id}`}
                  className="inline-flex items-center gap-1 font-medium text-neutral-700 transition-colors hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Zum Leitfaden</span>
                </Link>
              </li>
            </ol>

            {/* Quick Tool Switcher */}
            <div className="flex flex-wrap items-center gap-1 rounded-xl border border-neutral-200/80 bg-neutral-100/80 p-1 dark:border-neutral-800 dark:bg-neutral-900/80">
              {TOOL_SWITCHER_ITEMS.map((item) => {
                const active = item.id === id;
                return (
                  <Link
                    key={item.id}
                    href={`/tools/${item.id}`}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                      active
                        ? "bg-white text-neutral-900 shadow-2xs dark:bg-neutral-800 dark:text-white"
                        : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>

          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-neutral-900 dark:text-white">
                {title}
              </h1>
              {category && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${theme.badgeClass}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${theme.dotClass}`} />
                  {category}
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
              {tagline}
            </p>
          </div>
        </div>
      )}

      {/* Workbench Window Frame */}
      <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-white/95 shadow-xl shadow-neutral-900/5 backdrop-blur-md dark:border-neutral-800 dark:bg-neutral-900/90 dark:shadow-black/30">
        {/* Workbench Chrome Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200/80 bg-neutral-50/95 px-4 py-3 sm:px-5 dark:border-neutral-800 dark:bg-neutral-900/95">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>

            <span className="font-heading text-sm sm:text-base font-bold tracking-tight text-neutral-900 dark:text-white">
              {title}
            </span>

            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[10.5px] font-semibold text-emerald-700 dark:text-emerald-300">
              {statusBadge}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!standalone && !isFullscreen && (
              <button
                type="button"
                onClick={scrollToGuide}
                className="inline-flex min-h-[34px] items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 cursor-pointer"
              >
                <BookOpen className="h-3.5 w-3.5" />
                <span>Hintergrundwissen ↓</span>
              </button>
            )}

            {standalone && !isFullscreen && (
              <Link
                href={`/garden/${id}`}
                className="inline-flex min-h-[34px] items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
              >
                <BookOpen className="h-3.5 w-3.5" />
                <span>Zum Fachartikel</span>
              </Link>
            )}

            {!standalone && !isFullscreen && (
              <Link
                href={`/tools/${id}`}
                className="inline-flex min-h-[34px] items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                title="Als eigene Vollbild-Seite ohne Artikel öffnen"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Eigene Vollbild-Seite</span>
              </Link>
            )}

            <button
              type="button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              className="inline-flex min-h-[34px] items-center gap-1.5 rounded-xl bg-neutral-900 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200 cursor-pointer"
            >
              {isFullscreen ? (
                <>
                  <Minimize2 className="h-3.5 w-3.5" />
                  <span>Vollbild beenden (Esc)</span>
                </>
              ) : (
                <>
                  <Maximize2 className="h-3.5 w-3.5" />
                  <span>Vollbild</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Workbench Inner Content */}
        <div className="p-4 sm:p-6">{children}</div>
      </div>

      {/* Subtle Footer Bar under Workbench */}
      {!standalone && !isFullscreen && (
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-neutral-500 dark:text-neutral-400">
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
            <span>Alle Berechnungen laufen lokal im Browser – ohne Werbung oder Account-Zwang.</span>
          </span>
          <button
            type="button"
            onClick={scrollToGuide}
            className="inline-flex items-center gap-1 font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white cursor-pointer"
          >
            <span>Technische Erklärung &amp; Grenzen lesen ↓</span>
          </button>
        </div>
      )}
    </section>
  );
}
